import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// ============ Types ============

export interface Group {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  memberCount: number;
  permissionCount: number;
}

export interface GroupDetail {
  group: Omit<Group, 'memberCount' | 'permissionCount'>;
  members: Array<{
    id: string;
    email: string;
    name: string;
    avatarUrl: string | null;
    role: string;
    addedAt: string;
  }>;
  permissions: string[];
}

export interface PermissionCatalogue {
  permissions: Record<string, Array<{ code: string; description: string }>>;
}

// ============ Queries ============

export function useGroups() {
  return useQuery({
    queryKey: ['admin', 'groups'],
    queryFn: () => api.get<{ groups: Group[] }>('/admin/groups'),
  });
}

export function useGroup(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'groups', id],
    queryFn: () => api.get<GroupDetail>(`/admin/groups/${id}`),
    enabled: !!id,
  });
}

export function usePermissionCatalogue() {
  return useQuery({
    queryKey: ['admin', 'permissions-catalogue'],
    queryFn: () => api.get<PermissionCatalogue>('/admin/groups/permissions'),
    staleTime: 30 * 60 * 1000, // 30 minutes — catalogue rarely changes
  });
}

// ============ Mutations ============

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; name: string; description?: string }) =>
      api.post<{ group: Group }>('/admin/groups', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; name?: string; description?: string }) =>
      api.patch<{ group: Group }>(`/admin/groups/${id}`, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups', variables.id] });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/groups/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
    },
  });
}

export function useSetGroupPermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, permissions }: { groupId: string; permissions: string[] }) =>
      api.put(`/admin/groups/${groupId}/permissions`, { permissions }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      // Permissions may change for active users
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useAddGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.post(`/admin/groups/${groupId}/members`, { userId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.delete(`/admin/groups/${groupId}/members/${userId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}
